import { useEffect, useState } from "react";

/**
 * How long an answer may take before a loader appears at all (Welle 1, idea
 * 24). A loader that shows for a quick answer is a flash — 150–300 ms is the
 * common window; the middle of it is taken.
 */
export const LOADER_DELAY_MS = 200;

/**
 * How long a loader stands once it has appeared. An answer that comes in at
 * 210 ms would otherwise blink it away again after ten — a flicker that reads
 * as something going wrong.
 */
export const LOADER_MINIMUM_MS = 300;

/**
 * Whether to show a loader while `pending`: not before `delayMs`, and, once
 * shown, for at least `minimumMs` — also past the answer, so the caller keeps
 * its content back for as long as this says `true`. A loader that has stood
 * its minimum goes in the same render the answer arrives in.
 *
 * The timers turn it, never a synchronous set inside an effect
 * (`react-hooks/set-state-in-effect`). The minimum counts from the moment the
 * loader was due, not from the render that showed it. A later wait starts
 * from the delay again — also one that follows on the same tick, before any
 * timer could run (review of 18.10.2026): that reset is made in render, the
 * way React adjusts state to a changed prop. A wait that follows on a loader
 * still short of its minimum keeps that loader rather than blinking it out
 * and back in.
 */
export function useDelayedLoader(
  pending: boolean,
  delayMs: number = LOADER_DELAY_MS,
  minimumMs: number = LOADER_MINIMUM_MS,
): boolean {
  /** When the loader appeared in this wait; `null` before it did. */
  const [shownAt, setShownAt] = useState<number | null>(null);
  /** It has stood its minimum. */
  const [stood, setStood] = useState(false);
  /** `pending` as the last render saw it — a new wait is its turn to `true`. */
  const [wasPending, setWasPending] = useState(pending);

  if (pending !== wasPending) {
    setWasPending(pending);
    if (pending && shownAt !== null && stood) {
      setShownAt(null);
      setStood(false);
    }
  }

  useEffect(() => {
    if (!pending || shownAt !== null) return;
    const timer = window.setTimeout(() => setShownAt(Date.now()), delayMs);
    return () => window.clearTimeout(timer);
  }, [pending, shownAt, delayMs]);

  useEffect(() => {
    if (shownAt === null || stood) return;
    const timer = window.setTimeout(() => setStood(true), Math.max(0, shownAt + minimumMs - Date.now()));
    return () => window.clearTimeout(timer);
  }, [shownAt, stood, minimumMs]);

  return shownAt !== null && (pending || !stood);
}
